import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import PasswordGate from '../components/common/PasswordGate';
import EntryCard from '../components/EntryCard';
import FamilyTree from './Family/FamilyTree';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { cn, SECTIONS } from '../lib/utils';

const { FiFolder, FiCalendar, FiClock } = FiIcons;

const SectionPage = () => {
  const { sectionId } = useParams();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('years'); // 'grid', 'folders', 'years'
  
  const meta = SECTIONS[sectionId] || { name: sectionId };

  useEffect(() => {
    fetchEntries();
    // Default to grid for Family/Trips, Years for Luka/Klara
    if (sectionId === 'luka' || sectionId === 'klara') {
      setViewMode('years');
    } else if (sectionId === 'trips') {
      setViewMode('folders');
    } else {
      setViewMode('grid');
    }
  }, [sectionId]);

  const fetchEntries = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('entries_20240520')
      .select('*')
      .eq('section', sectionId)
      .eq('status', 'published')
      .order('date', { ascending: false });
    
    setEntries(data || []);
    setLoading(false);
  };

  // Grouping Logic
  const groupedByFolder = entries.reduce((acc, entry) => {
    const folder = entry.folder_name || 'General';
    if (!acc[folder]) acc[folder] = [];
    acc[folder].push(entry);
    return acc;
  }, {});

  const groupedByYear = entries.reduce((acc, entry) => {
    const year = new Date(entry.date).getFullYear();
    if (!acc[year]) acc[year] = [];
    acc[year].push(entry);
    return acc;
  }, {});

  const years = Object.keys(groupedByYear).sort((a, b) => b - a);

  return (
    <PasswordGate section={sectionId}>
      <div className="max-w-5xl mx-auto px-4 py-12">
        <header className="mb-12">
          <div className={cn("inline-block px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider mb-4", meta.color)}>
            {sectionId} Collection
          </div>
          <h1 className="text-5xl font-serif font-bold capitalize mb-6">{meta.name}</h1>
          
          <div className="flex flex-wrap gap-3 mt-4">
            <button 
              onClick={() => setViewMode('grid')}
              className={cn(
                "px-5 py-2 rounded-full text-xs font-bold transition-all border",
                viewMode === 'grid' ? "bg-[#2c2c2c] text-white border-[#2c2c2c]" : "bg-white text-stone-500 border-stone-200"
              )}
            >
              Recent Feed
            </button>
            <button 
              onClick={() => setViewMode('years')}
              className={cn(
                "px-5 py-2 rounded-full text-xs font-bold transition-all border",
                viewMode === 'years' ? "bg-[#2c2c2c] text-white border-[#2c2c2c]" : "bg-white text-stone-500 border-stone-200"
              )}
            >
              By Year
            </button>
            {sectionId === 'trips' && (
              <button 
                onClick={() => setViewMode('folders')}
                className={cn(
                  "px-5 py-2 rounded-full text-xs font-bold transition-all border",
                  viewMode === 'folders' ? "bg-[#2c2c2c] text-white border-[#2c2c2c]" : "bg-white text-stone-500 border-stone-200"
                )}
              >
                By Trip
              </button>
            )}
          </div>
        </header>

        {sectionId === 'family' && viewMode === 'grid' && (
          <section className="mb-16 pb-16 border-b border-stone-100">
             <h2 className="text-3xl font-serif font-bold mb-2">Our Roots</h2>
             <p className="text-stone-500 mb-10">The branches that make our family whole.</p>
             <FamilyTree />
          </section>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-pulse">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-64 bg-stone-100 rounded-[2rem]" />)}
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-20 bg-stone-50 rounded-[3rem] border-2 border-dashed border-stone-200">
            <p className="text-stone-400 font-serif italic text-xl">No memories have been recorded in this chapter yet.</p>
          </div>
        ) : (
          <div className="space-y-16">
            {viewMode === 'years' && years.map(year => (
              <div key={year}>
                <h3 className="text-3xl font-serif font-bold mb-8 flex items-center gap-4">
                  <span className="text-[#8c7851]">{year}</span>
                  <span className="h-px bg-stone-200 flex-1"></span>
                  <span className="text-[10px] font-bold text-stone-300 uppercase tracking-[0.2em]">{groupedByYear[year].length} Stories</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {groupedByYear[year].map(entry => <EntryCard key={entry.id} entry={entry} />)}
                </div>
              </div>
            ))}

            {viewMode === 'folders' && Object.entries(groupedByFolder).map(([folder, folderEntries]) => (
              <div key={folder}>
                <h3 className="text-2xl font-serif font-bold mb-8 flex items-center gap-3">
                  <div className="w-10 h-10 bg-stone-100 rounded-xl flex items-center justify-center text-[#8c7851]">
                    <SafeIcon icon={FiFolder} />
                  </div>
                  {folder}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {folderEntries.map(entry => <EntryCard key={entry.id} entry={entry} />)}
                </div>
              </div>
            ))}

            {viewMode === 'grid' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {entries.map(entry => <EntryCard key={entry.id} entry={entry} />)}
              </div>
            )}
          </div>
        )}
      </div>
    </PasswordGate>
  );
};

export default SectionPage;